import React, {useState, useRef, type FC } from 'react';
import { Employee2, StatusType } from '../../../../model/Employee';
import { Theme } from '../../../../pages/Portal';

export const FormField: FC<{ label: string; full?: boolean; children: React.ReactNode }> = ({ label, full, children }) => (
  <div className={full ? 'form-group full' : 'form-group'}>
    <label className="form-label">{label}</label>
    {children}
  </div>
);

export const StatusBadge = ({ type, theme = 'light' } : { type: StatusType; theme?: Theme }) => {
  const styles: Record<StatusType, string> = {
    Tetap: 'bg-[#00d4aa]/10 text-[#00d4aa] border-[#00d4aa]/20',
    Kontrak: 'bg-[#0095ff]/10 text-[#0095ff] border-[#0095ff]/20',
    Probation: 'bg-[#ffd166]/10 text-[#ffd166] border-[#ffd166]/20',
    Magang: 'bg-[#ff6b6b]/10 text-[#ff6b6b] border-[#ff6b6b]/20',
  };
  return (
    <div className={`px-2 py-0.5 ${styles[type]} solid`} style={{ fontFamily: "monospace" }}>
      {type}
    </div>
  );
};

export const fetchPutKaryawan = async (data : Omit<Employee2, 'id'>) => {
    try {
        const myHeaders = new Headers();
        myHeaders.append("Content-Type", "application/json");
        myHeaders.append("Authorization", `${process.env.REACT_APP_API_KEY}`);
        const requestOptions = { method: "PUT", headers: myHeaders, body: JSON.stringify(data), redirect: "follow" as RequestRedirect };
        const response = await fetch(process.env.REACT_APP_API_URL_V2 + "/karyawan", requestOptions);
        const result = await response.json();
        return result;
    } catch (err) {
        console.error("Error fetching PUT Karyawan:", err);
    }
}

export const fetchGetKota = async () => {
  try {
    const myHeaders = new Headers();
    myHeaders.append("Content-Type", "application/json");
    myHeaders.append("Authorization", `${process.env.REACT_APP_API_KEY}`);
    const requestOptions = { method: "GET", headers: myHeaders, redirect: "follow" as RequestRedirect };
    const response = await fetch(process.env.REACT_APP_API_URL_V2 + "/karyawan/kota", requestOptions);
    const result = await response.json().catch(() => ({}));
    return result.data ?? [];
  } catch (err: any) {
    console.error("Error fetching GET Karyawan:", err);
    return [];
  }
}

export const fetchPostKaryawan = async (data : Omit<Employee2, 'id'>) => {
    try {
        const myHeaders = new Headers();
        myHeaders.append("Content-Type", "application/json");
        myHeaders.append("Authorization", `${process.env.REACT_APP_API_KEY}`);
        const requestOptions = { method: "POST", headers: myHeaders, body: JSON.stringify(data), redirect: "follow" as RequestRedirect };
        const response = await fetch(process.env.REACT_APP_API_URL_V2 + "/karyawan", requestOptions);
        const result = await response.json();
        return result;
    } catch (err) {
        console.error("Error fetching PUT Karyawan:", err);
    }
}

export const fetchDeleteKaryawan = async (nik: string, nama: string) => {
  try {
    const myHeaders = new Headers();
    myHeaders.append("Content-Type", "application/json");
    myHeaders.append("Authorization", `${process.env.REACT_APP_API_KEY}`);
    const requestOptions = { method: "DELETE", headers: myHeaders, redirect: "follow" as RequestRedirect };
    const response = await fetch(`${process.env.REACT_APP_API_URL_V2}/karyawan?nik=${nik}&nama=${encodeURIComponent(nama)}`, requestOptions);
    const result = await response.json();
    return result;
  } catch (err) {
    console.error("Error fetching DELETE Karyawan:", err);
  }
};

export const makeInitials = (nama: string): string => {
  return nama.split(' ').map(n => n[0]).join('').toUpperCase().slice(0, 2);
};

export const calculateAge = (birthDate: string): number => {
  if (!birthDate) return 0;
  const date = new Date(birthDate);
  if (Number.isNaN(date.getTime())) return 0;
  const today = new Date();
  let age = today.getFullYear() - date.getFullYear();
  const monthDiff = today.getMonth() - date.getMonth();
  if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < date.getDate())) {
    age -= 1;
  }
  return age;
};

export const PhotoUploadField: FC<{
  value: string;
  onChange: (dataUrl: string) => void;
  isDark?: boolean;
  employeeName?: string;
}> = ({ value, onChange, isDark = false, employeeName = '' }) => {
  const [isDragging, setIsDragging] = useState(false);
  const [imageLoaded, setImageLoaded] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  // Smart URL detection
  const isBase64 = value && value.startsWith('data:') || value.includes('/storage/');
  const displayUrl = isBase64 ? value : `${process.env.REACT_APP_API_URL}/storage/route/karyawan/${encodeURIComponent(employeeName)}/${value}`;

  const handleFile = (file: File) => {
    if (file && file.type.startsWith('image/')) {
      setImageLoaded(false);
      const reader = new FileReader();
      reader.onload = (e) => {
        const result = e.target?.result as string;
        onChange(result);
      };
      reader.readAsDataURL(file);
    }
  };

  const handleDragOver = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(true);
  };

  const handleDragLeave = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
  };

  const handleDrop = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
    
    const files = e.dataTransfer.files;
    if (files.length > 0) {
      handleFile(files[0]);
    }
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files;
    if (files && files.length > 0) {
      handleFile(files[0]);
    }
  };

  return (
    <div
      onDragOver={handleDragOver}
      onDragLeave={handleDragLeave}
      onDrop={handleDrop}
      onClick={() => fileInputRef.current?.click()}
      className={`relative w-full border-2 border-dashed rounded-lg p-6 cursor-pointer transition-all ${
        isDragging
          ? 'border-[#00d4aa] bg-[#00d4aa]/10'
          : isDark
          ? 'border-[#30363d] bg-[#0d1117]/50 hover:border-[#00d4aa]/50'
          : 'border-gray-300 bg-gray-50 hover:border-[#00d4aa]/50'
      }`}
    >
      <input
        ref={fileInputRef}
        type="file"
        accept="image/*"
        onChange={handleInputChange}
        className="hidden"
      />

      {value ? (
        <div className="flex flex-col items-center gap-3">
          <div className="relative w-32 h-32 rounded-lg overflow-hidden border border-[#00d4aa]/30">
            {!imageLoaded && (
              <div className="absolute inset-0 bg-gray-200 dark:bg-gray-700 flex items-center justify-center">
                <span className="text-sm text-gray-500">Loading...</span>
              </div>
            )}
            <img
              src={displayUrl}
              alt="Preview"
              className="w-full h-full object-cover"
              onLoad={() => setImageLoaded(true)}
              onError={() => setImageLoaded(true)}
            />
          </div>
          <div className="text-sm text-center">
            <p className={`font-medium ${isDark ? 'text-[#e6edf3]' : 'text-gray-900'}`}>
              Foto sudah dipilih
            </p>
            <p className={`text-xs ${isDark ? 'text-[#8b949e]' : 'text-gray-600'}`}>
              Klik atau drag foto baru untuk mengganti
            </p>
          </div>
          <button
            type="button"
            onClick={(e) => {
              e.stopPropagation();
              onChange('');
              setImageLoaded(false);
              if (fileInputRef.current) {
                fileInputRef.current.value = '';
              }
            }}
            className="px-3 py-1 text-xs bg-red-500 text-white rounded hover:bg-red-600 transition-colors"
          >
            Hapus Foto
          </button>
        </div>
      ) : (
        <div className="flex flex-col items-center gap-2 py-4">
          <div className={`text-3xl ${isDark ? 'text-[#6e7681]' : 'text-gray-400'}`}>
            📷
          </div>
          <p className={`text-sm font-medium ${isDark ? 'text-[#e6edf3]' : 'text-gray-900'}`}>
            Drag foto disini atau klik untuk upload
          </p>
          <p className={`text-xs ${isDark ? 'text-[#8b949e]' : 'text-gray-600'}`}>
            Format: JPG, PNG, GIF, WebP
          </p>
        </div>
      )}
    </div>
  );
};